// Shared utilization math for the Utilization card and the summary slide, so both
// report the same numbers: run minutes against a 40h/week target per machine,
// grouped by ISO week (Monday start) and prorated at the window boundaries.
export const EXPECTED_HOURS_PER_WEEK = 40;
const EXPECTED_MINUTES_PER_WEEK = EXPECTED_HOURS_PER_WEEK * 60;

export function getWeekStart(dateStr) {
  const d = new Date(dateStr + 'T00:00:00Z');
  const day = d.getUTCDay(); // 0=Sun, 1=Mon, ...
  const diff = day === 0 ? -6 : 1 - day; // shift back to Monday
  const monday = new Date(d);
  monday.setUTCDate(d.getUTCDate() + diff);
  return monday.toISOString().slice(0, 10);
}

export function formatWeekLabel(weekStart) {
  const d = new Date(weekStart + 'T00:00:00Z');
  const endD = new Date(d);
  endD.setUTCDate(d.getUTCDate() + 6);
  const MONTHS = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  const startStr = `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}`;
  if (d.getUTCMonth() === endD.getUTCMonth()) {
    return `${startStr}–${endD.getUTCDate()}`;
  }
  return `${startStr} – ${MONTHS[endD.getUTCMonth()]} ${endD.getUTCDate()}`;
}

// Sum a { 'YYYY-MM-DD': minutes } map into { weekStart: minutes }.
function groupByWeek(dailyMap = {}) {
  const weekMap = {};
  Object.entries(dailyMap).forEach(([dateStr, minutes]) => {
    const ws = getWeekStart(dateStr);
    weekMap[ws] = (weekMap[ws] || 0) + minutes;
  });
  return weekMap;
}

// errorData is optional; when given, each week (and machine-week) also carries
// downMinutes, the downtime part of its tracked minutes.
export function computeUtilization({ usageData, errorData, machineCount = 1, startDate, endDate }) {
  if (!usageData?.daily_usage) {
    return {
      totalUtilization: 0,
      weeks: [],
      totalRunMinutes: 0,
      totalExpectedMinutes: 0,
      periodWeeks: 0,
      machineWeeks: {},
    };
  }

  const now = new Date();
  const startDt = startDate
    ? new Date(startDate + 'T00:00:00Z')
    : (() => {
        // fallback: 3-month window
        let sm = now.getUTCMonth() - 2;
        let sy = now.getUTCFullYear();
        if (sm < 0) {
          sm += 12;
          sy -= 1;
        }
        return new Date(Date.UTC(sy, sm, 1));
      })();
  const endDt = endDate
    ? (() => {
        const d = new Date(endDate + 'T00:00:00Z');
        d.setUTCDate(d.getUTCDate() + 1);
        return d;
      })()
    : now;
  const effectiveEnd = endDt > now ? now : endDt;

  const dailyUsage = usageData.daily_usage;
  const totalRunMinutes = usageData.total_minutes || 0;

  // Total expected: fraction of weeks in the window × 40h × number of machines
  const effectiveMachineCount = machineCount > 0 ? machineCount : 1;
  const periodDays = Math.max(
    0,
    (effectiveEnd.getTime() - startDt.getTime()) / (1000 * 60 * 60 * 24)
  );
  const periodWeeks = periodDays / 7;
  const totalExpectedMinutes = periodWeeks * EXPECTED_MINUTES_PER_WEEK * effectiveMachineCount;
  const totalUtilization = totalExpectedMinutes > 0 ? totalRunMinutes / totalExpectedMinutes : 0;

  // Group daily data by ISO week (Monday start)
  const weekMap = groupByWeek(dailyUsage);
  const downWeekMap = groupByWeek(errorData?.daily_downtime);

  // Compute per-week utilization with boundary prorating
  const weeks = Object.entries(weekMap)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([weekStart, runMinutes]) => {
      const weekStartDate = new Date(weekStart + 'T00:00:00Z');
      const weekEndDate = new Date(weekStartDate);
      weekEndDate.setUTCDate(weekStartDate.getUTCDate() + 7); // exclusive end: next Monday

      // Clamp week to the actual window boundaries
      const clampedStart = new Date(Math.max(weekStartDate.getTime(), startDt.getTime()));
      const clampedEnd = new Date(Math.min(weekEndDate.getTime(), effectiveEnd.getTime()));
      const daysInWindow = Math.max(
        0,
        Math.ceil((clampedEnd.getTime() - clampedStart.getTime()) / (1000 * 60 * 60 * 24))
      );
      if (daysInWindow === 0) {
        return null;
      }
      const expectedMinutes =
        (daysInWindow / 7) * EXPECTED_MINUTES_PER_WEEK * effectiveMachineCount;

      return {
        weekStart,
        label: formatWeekLabel(weekStart),
        runMinutes,
        downMinutes: Math.min(downWeekMap[weekStart] || 0, runMinutes),
        expectedMinutes,
        utilization: expectedMinutes > 0 ? runMinutes / expectedMinutes : 0,
      };
    })
    .filter(Boolean);

  // Per-machine weekly data for drill-down
  const machineWeeks = {};
  if (usageData.machine_daily_usage && usageData.machines) {
    Object.entries(usageData.machine_daily_usage).forEach(([machineId, dailyMap]) => {
      const mWeekMap = groupByWeek(dailyMap);
      const mDownWeekMap = groupByWeek(errorData?.machine_daily_downtime?.[machineId]);
      Object.entries(mWeekMap).forEach(([ws, runMins]) => {
        const info = usageData.machines[machineId] || {};
        const machineLabel = info.nickname || info.serial || `Machine ${machineId}`;
        const weekStartDate = new Date(ws + 'T00:00:00Z');
        const weekEndDate = new Date(weekStartDate);
        weekEndDate.setUTCDate(weekStartDate.getUTCDate() + 7); // exclusive end: next Monday
        const clampedStart = new Date(Math.max(weekStartDate.getTime(), startDt.getTime()));
        const clampedEnd = new Date(Math.min(weekEndDate.getTime(), effectiveEnd.getTime()));
        const daysInWindow = Math.max(
          0,
          Math.ceil((clampedEnd.getTime() - clampedStart.getTime()) / (1000 * 60 * 60 * 24))
        );
        if (daysInWindow === 0) {
          return;
        }
        const mExpected = (daysInWindow / 7) * EXPECTED_MINUTES_PER_WEEK;
        if (!machineWeeks[ws]) {
          machineWeeks[ws] = [];
        }
        machineWeeks[ws].push({
          machineId,
          machineLabel,
          runMinutes: runMins,
          downMinutes: Math.min(mDownWeekMap[ws] || 0, runMins),
          utilization: mExpected > 0 ? runMins / mExpected : 0,
        });
      });
    });
    Object.values(machineWeeks).forEach(arr => arr.sort((a, b) => b.runMinutes - a.runMinutes));
  }

  return {
    totalUtilization,
    weeks,
    totalRunMinutes,
    totalExpectedMinutes,
    periodWeeks,
    machineWeeks,
  };
}
